import React from "react";
import { Link } from "react-router-dom";
import styles from "../../styles/HomePage.module.css";
import journeyStyles from "../../styles/LearningJourney.module.css";
import { ROUTES } from "../../config/routes";

export default function LearningJourneySection() {
    return (
        <section className={journeyStyles.learningJourney}>
            <div className={journeyStyles.journeyHeader}>
                <h2 className={styles.sectionTitle}>The Journey to Playing By Ear</h2>
                <p className={journeyStyles.journeyIntro}>
                    You hear a song and think "I wish I could just play that." It feels impossible—like you'd need
                    to be born with a gift. But here's the truth: <strong>playing by ear isn't magic. It's a collection
                    of skills that anyone can learn</strong>, step by step.
                </p>
                <p className={journeyStyles.journeySubtext}>
                    The challenge isn't talent. It's knowing what to practice, in what order, and having the patience
                    to build each skill. We've mapped out the exact path from "I can't hear anything" to "I can play
                    what I hear."
                </p>
            </div>

            <div className={journeyStyles.milestoneTimeline}>
                {/* Foundation */}
                <div className={journeyStyles.milestone}>
                    <div className={journeyStyles.milestoneMarker}>
                        <div className={journeyStyles.milestoneNumber}>1</div>
                        <div className={journeyStyles.milestoneLine}></div>
                    </div>
                    <div className={journeyStyles.milestoneContent}>
                        <div className={journeyStyles.milestoneStage}>Foundation</div>
                        <h3 className={journeyStyles.milestoneTitle}>Matching Pitch & Basic Intervals</h3>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>Why it matters:</strong> If you can't hear the difference between two notes, you can't recreate melodies.
                            This is where you build the connection between what you hear and what you play.
                        </p>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>How to train:</strong> Listen to two notes, identify if the second one goes up or down, then figure out by how much.
                            Start with big jumps (octaves, fifths) before moving to smaller ones.
                        </p>
                        <div className={journeyStyles.milestoneSkills}>
                            <div className={journeyStyles.skillTag}>Match single pitches</div>
                            <div className={journeyStyles.skillTag}>Recognize octaves, fifths, fourths</div>
                            <div className={journeyStyles.skillTag}>Sing what you hear</div>
                        </div>
                        <div className={journeyStyles.practiceSection}>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>📱 Use These Tools</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li><Link to={ROUTES.DRILL.INTERVALS}>Intervals Drill</Link> - Start with octaves, fifths, fourths</li>
                                </ul>
                            </div>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>🎹 Practice On Your Instrument</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li>Play a note, sing it back, find it again</li>
                                    <li>Listen to simple melodies and try to match them</li>
                                </ul>
                            </div>
                        </div>
                        <div className={journeyStyles.milestoneTime}>2-4 weeks with daily practice</div>
                    </div>
                </div>

                {/* Building Blocks */}
                <div className={journeyStyles.milestone}>
                    <div className={journeyStyles.milestoneMarker}>
                        <div className={journeyStyles.milestoneNumber}>2</div>
                        <div className={journeyStyles.milestoneLine}></div>
                    </div>
                    <div className={journeyStyles.milestoneContent}>
                        <div className={journeyStyles.milestoneStage}>Building Blocks</div>
                        <h3 className={journeyStyles.milestoneTitle}>All Intervals & Simple Melodies</h3>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>Why it matters:</strong> Every melody you've ever heard is just a sequence of intervals. Once you can identify all 12 interval types,
                            you can figure out any melody note by note.
                        </p>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>How to train:</strong> Practice identifying each interval type (major 3rd, minor 6th, etc.)
                            until you recognize them automatically. Then apply this by learning simple songs and nursery rhymes purely by ear.
                        </p>
                        <div className={journeyStyles.milestoneSkills}>
                            <div className={journeyStyles.skillTag}>Identify all 12 intervals</div>
                            <div className={journeyStyles.skillTag}>Figure out simple melodies</div>
                            <div className={journeyStyles.skillTag}>Recognize scale patterns</div>
                        </div>
                        <div className={journeyStyles.practiceSection}>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>📱 Use These Tools</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li><Link to={ROUTES.DRILL.INTERVALS}>Intervals Drill</Link> - Practice all 12 intervals until automatic</li>
                                </ul>
                            </div>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>🎹 Practice On Your Instrument</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li>Learn simple songs by ear (nursery rhymes, pop melodies)</li>
                                    <li>Play intervals on your instrument and sing them back</li>
                                </ul>
                            </div>
                        </div>
                        <div className={journeyStyles.milestoneTime}>6-8 weeks of consistent training</div>
                    </div>
                </div>

                {/* Harmony */}
                <div className={journeyStyles.milestone}>
                    <div className={journeyStyles.milestoneMarker}>
                        <div className={journeyStyles.milestoneNumber}>3</div>
                        <div className={journeyStyles.milestoneLine}></div>
                    </div>
                    <div className={journeyStyles.milestoneContent}>
                        <div className={journeyStyles.milestoneStage}>Harmony</div>
                        <h3 className={journeyStyles.milestoneTitle}>Chord Recognition</h3>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>Why it matters:</strong> Most songs are built on chord progressions, not just melodies. Being able to hear "that's a C major chord"
                            or "that's G minor" means you can play accompaniment to any song.
                        </p>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>How to train:</strong> Start by distinguishing major (happy) from minor (sad).
                            Chords are just multiple intervals played together, so your interval training makes this much easier. Gradually add diminished, augmented, and 7th chords.
                        </p>
                        <div className={journeyStyles.milestoneSkills}>
                            <div className={journeyStyles.skillTag}>Major vs minor instantly</div>
                            <div className={journeyStyles.skillTag}>Diminished & augmented chords</div>
                            <div className={journeyStyles.skillTag}>7th chords and extensions</div>
                        </div>
                        <div className={journeyStyles.practiceSection}>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>📱 Use These Tools</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li><Link to={ROUTES.DRILL.CHORDS}>Chords Drill</Link> - Start with major/minor, add dim/aug later</li>
                                </ul>
                            </div>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>🎹 Practice On Your Instrument</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li>Play songs focusing only on chord changes</li>
                                    <li>Listen to music and try to identify chord qualities</li>
                                </ul>
                            </div>
                        </div>
                        <div className={journeyStyles.milestoneTime}>3-4 months to feel confident</div>
                    </div>
                </div>

                {/* Musical Context */}
                <div className={journeyStyles.milestone}>
                    <div className={journeyStyles.milestoneMarker}>
                        <div className={journeyStyles.milestoneNumber}>4</div>
                        <div className={journeyStyles.milestoneLine}></div>
                    </div>
                    <div className={journeyStyles.milestoneContent}>
                        <div className={journeyStyles.milestoneStage}>Musical Context</div>
                        <h3 className={journeyStyles.milestoneTitle}>Chord Progressions & Songs</h3>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>Why it matters:</strong> Thousands of songs use the same 5-10 chord progressions (I-V-vi-IV, I-IV-V, etc.). Once you recognize these patterns,
                            you can play along to songs you've never heard before within seconds.
                        </p>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>How to train:</strong> Learn to identify common progressions by ear,
                            then practice finding the key of a song. Once you know the key and progression, you can play the whole song.
                        </p>
                        <div className={journeyStyles.milestoneSkills}>
                            <div className={journeyStyles.skillTag}>Identify common progressions</div>
                            <div className={journeyStyles.skillTag}>Figure out song keys</div>
                            <div className={journeyStyles.skillTag}>Play songs you've never practiced</div>
                        </div>
                        <div className={journeyStyles.practiceSection}>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>📱 Use These Tools</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li><Link to={ROUTES.DRILL.PROGRESSIONS}>Progressions Drill</Link> - Learn I-V-vi-IV, I-IV-V, and more</li>
                                </ul>
                            </div>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>🎹 Practice On Your Instrument</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li>Figure out full songs by ear from start to finish</li>
                                    <li>Play along with recordings, transposing to different keys</li>
                                </ul>
                            </div>
                        </div>
                        <div className={journeyStyles.milestoneTime}>6-12 months to master</div>
                    </div>
                </div>

                {/* Mastery */}
                <div className={journeyStyles.milestone}>
                    <div className={journeyStyles.milestoneMarker}>
                        <div className={journeyStyles.milestoneNumber}>5</div>
                    </div>
                    <div className={journeyStyles.milestoneContent}>
                        <div className={journeyStyles.milestoneStage}>Mastery</div>
                        <h3 className={journeyStyles.milestoneTitle}>Instant Translation: Ear to Instrument</h3>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>Why it matters:</strong> At this level, there's no gap between hearing and playing. You can improvise solos, transcribe music in real-time,
                            and jam with other musicians without sheet music or preparation.
                        </p>
                        <p className={journeyStyles.milestoneDescription}>
                            <strong>How to train:</strong> Keep pushing yourself with all three skills
                            (intervals, chords, progressions) in harder contexts—faster tempos, unfamiliar genres, complex jazz harmonies. Play along with recordings and jam sessions constantly.
                        </p>
                        <div className={journeyStyles.milestoneSkills}>
                            <div className={journeyStyles.skillTag}>Real-time transcription</div>
                            <div className={journeyStyles.skillTag}>Improvisation in any key</div>
                            <div className={journeyStyles.skillTag}>Jam with anyone, anywhere</div>
                        </div>
                        <div className={journeyStyles.practiceSection}>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>📱 Use These Tools</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li>Mix all drills - <Link to={ROUTES.DRILL.INTERVALS}>Intervals</Link>, <Link to={ROUTES.DRILL.CHORDS}>Chords</Link>, <Link to={ROUTES.DRILL.PROGRESSIONS}>Progressions</Link></li>
                                    <li>Challenge yourself with endless mode and faster tempos</li>
                                </ul>
                            </div>
                            <div className={journeyStyles.practiceColumn}>
                                <h4 className={journeyStyles.practiceTitle}>🎹 Practice On Your Instrument</h4>
                                <ul className={journeyStyles.practiceList}>
                                    <li>Improvise solos over backing tracks in any key</li>
                                    <li>Join jam sessions, play unfamiliar songs on the spot</li>
                                </ul>
                            </div>
                        </div>
                        <div className={journeyStyles.milestoneTime}>12-18 months of dedicated practice</div>
                    </div>
                </div>
            </div>
        </section>
    );
}
